import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatOption,
  MatSelectChange,
  MatSelectModule,
} from '@angular/material/select';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import {
  FilterParams,
  PaginationParams,
  ResourcesService,
} from '../_services/resources.service';
import { CommonModule, DatePipe } from '@angular/common';
import { EditResourceComponent } from '../forms/edit-resource-form/edit-resource-form.component';
import { SharedService } from '../_services/shared.service';
import { AuthService } from '../_services/auth.service';
import { LifecycleFormComponent } from '../forms/lifecycle-form/lifecycle-form.component';
import { DeleteResourceFormComponent } from '../forms/delete-resource-form/delete-resource-form.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { createDefaultFilter, ResourceFilter } from './resourceFilter';
import { ResourceDto, ResourceResDto } from '../_dtos/resourceResDto';
import { ResourceTypes } from '../_enums/resourceTypes';
import { ResourceStatus } from '../_enums/resourceStatus';
import { FilterFormData } from '../_models/filterFormData';
import { debounceTime, distinctUntilChanged, filter, Subscription } from 'rxjs';
import { TableDataService } from '../_services/table-data.service';
import { PaginationState } from './paginationState';
import { TextCardComponent } from '../shared/text-card/text-card.component';

@Component({
  selector: 'app-my-resources',
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatOption,
    MatSelectModule,
    MatButton,
    MatTooltip,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconButton,
    TextCardComponent,
  ],
  templateUrl: './my-resources.component.html',
  standalone: true,
  styleUrl: './my-resources.component.css',
  providers: [
    DatePipe,
    {
      provide: MatPaginatorIntl,
      useFactory: CustomPaginator,
      deps: [TranslateService],
    },
  ],
})
export class MyResourcesComponent implements OnInit, AfterViewInit, OnDestroy {
  public tableDataSource = new MatTableDataSource<ResourceDto>([]);
  public tableColumns: string[] = [
    'resourceType',
    'displayName',
    'primaryOwner',
    'secondaryOwner',
    'resourceStatus',
    'endOfLife',
    'actions',
  ];
  private filters: ResourceFilter = createDefaultFilter();
  public userPrincipalName!: string;
  public filterMenu!: FormGroup;
  public selectedRow: ResourceDto | null = null;
  public dataIsLoaded: boolean = false;

  private paginationState: PaginationState = {
    pageSize: 10,
    totalCount: 0,
    currentPageIndex: 0,
  };

  private resourceUpdateSubscription!: Subscription;
  private resourceDeleteSubscription!: Subscription;
  private searchSubscription?: Subscription;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private resourcesService: ResourcesService,
    private sharedService: SharedService,
    private authService: AuthService,
    private tableDataService: TableDataService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.userPrincipalName = this.authService.getUsername();
    this.getUnfilteredData();
    this.subscribeToDataChanges();
    this.setupDebouncedSearch();
  }

  ngAfterViewInit() {
    this.updatePaginatorState();
  }

  ngOnDestroy() {
    if (this.resourceUpdateSubscription)
      this.resourceUpdateSubscription.unsubscribe();
    if (this.resourceDeleteSubscription)
      this.resourceDeleteSubscription.unsubscribe();
    if (this.searchSubscription) this.searchSubscription.unsubscribe();
  }

  private initializeForm() {
    this.filterMenu = new FormGroup<FilterFormData>({
      search: new FormControl(),
      resourceType: new FormControl(),
      resourceStatus: new FormControl(),
    });
  }

  private initializeTable(data: ResourceDto[]) {
    this.tableDataSource = new MatTableDataSource<ResourceDto>(data);

    this.changeDetectorRef.detectChanges();
    if (this.sort) this.tableDataSource.sort = this.sort;

    this.tableDataSource.sortingDataAccessor = (
      item: ResourceDto,
      property: string,
    ): string => {
      switch (property) {
        case 'displayName':
          return item.displayName.toLocaleLowerCase();
        default:
          return '';
      }
    };
  }

  private updatePaginatorState() {
    if (this.paginator) {
      this.paginator.length = this.paginationState.totalCount;
      this.paginator.pageSize = this.paginationState.pageSize;
      this.paginator.pageIndex = this.paginationState.currentPageIndex;
    }
  }

  private getUnfilteredData(pageIndex: number = 0) {
    const offset = pageIndex * this.paginationState.pageSize;
    const pagination: PaginationParams = {
      pageSize: this.paginationState.pageSize,
      startIndex: offset > 0 ? offset : undefined,
    };

    this.resourcesService.getMyResources(pagination).subscribe((data) => {
      this.handleApiResponse(data);
      this.dataIsLoaded = true;
    });
  }

  private handleApiResponse(data: ResourceResDto) {
    this.initializeTable(data.resources);

    // Update pagination state
    this.paginationState.totalCount = data.totalCount || 0;
    this.updatePaginatorState();
  }

  private subscribeToDataChanges() {
    this.resourceUpdateSubscription =
      this.tableDataService.resourceUpdated.subscribe((update) => {
        this.refreshCurrentPage();
      });

    this.resourceDeleteSubscription =
      this.tableDataService.resourceDeleted.subscribe((id) => {
        this.refreshCurrentPage();
      });
  }

  // Why does a page refresh update filters?
  private refreshCurrentPage() {
    if (this.hasActiveFilters()) this.getFilteredData();
    else this.getUnfilteredData(this.paginationState.currentPageIndex);
  }

  filterSearch() {
    this.filters.search =
      this.filterMenu.get('search')?.value?.trim().toLowerCase() || '';

    // Filter changed -> start from page 1;
    this.paginationState.currentPageIndex = 0;
    this.getFilteredData();
  }

  filterStatus(event: MatSelectChange) {
    this.filters.status = event.value?.trim().toLowerCase();

    // Filter changed -> start from page 1;
    this.paginationState.currentPageIndex = 0;
    this.getFilteredData();
  }

  filterType(event: MatSelectChange) {
    this.filters.type = event.value?.trim().toLowerCase();

    // Filter changed -> start from page 1;
    this.paginationState.currentPageIndex = 0;
    this.getFilteredData();
  }

  private getFilteredData() {
    const filters: FilterParams = {};

    if (this.filters.search !== '') filters.searchText = this.filters.search;
    if (this.filters.type !== '') filters.typeFilter = this.filters.type;
    if (this.filters.status !== '') filters.statusFilter = this.filters.status;

    const offset =
      this.paginationState.currentPageIndex * this.paginationState.pageSize;
    const pagination: PaginationParams = {
      pageSize: this.paginationState.pageSize,
      startIndex: offset > 0 ? offset : undefined,
    };

    this.resourcesService.filterMyResources(filters, pagination).subscribe({
      next: (data) => {
        this.handleApiResponse(data);
      },
    });
  }

  clearFilter() {
    this.filters = { search: '', status: '', type: '' };
    this.filterMenu.reset(this.filterMenu);

    this.paginationState.currentPageIndex = 0;
    this.getUnfilteredData();
  }

  onPageChange(event: PageEvent) {
    const targetPageIndex = event.pageIndex;
    const newPageSize = event.pageSize;
    const oldPageSize = this.paginationState.pageSize;

    this.paginationState.pageSize = newPageSize;

    // If page size changed, we need to recalculate and reload from page 0
    if (newPageSize !== oldPageSize) this.paginationState.currentPageIndex = 0;
    else this.paginationState.currentPageIndex = targetPageIndex;

    if (this.hasActiveFilters()) this.getFilteredData();
    else this.getUnfilteredData(this.paginationState.currentPageIndex);
  }

  private hasActiveFilters(): boolean {
    return !!(this.filters.search || this.filters.type || this.filters.status);
  }

  private setupDebouncedSearch() {
    this.searchSubscription = this.filterMenu
      .get('search')
      ?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((value) => value !== null),
      )
      .subscribe(() => {
        this.filterSearch();
      });
  }

  clickedRow(row: ResourceDto): void {
    this.selectedRow = this.selectedRow === row ? null : row;
  }

  openEditResourceForm(element: ResourceDto) {
    const formData = {
      formRef: EditResourceComponent,
      displayName: element.displayName,
      primaryOwner: element.primaryOwner,
      secondaryOwner: element.secondaryOwner,
      id: element.id,
      resourceType: element.resourceType,
      isGroupConnected: element.isGroupConnected,
      connectedGroupId: element.groupId,
    };
    this.sharedService.toggleEditResourceModal('Edit Resource', formData);
  }

  openLifecycleForm(element: ResourceDto) {
    const formData = {
      formRef: LifecycleFormComponent,
      displayName: element.displayName,
      endOfLife: element.endOfLife,
      id: element.id,
      resourceType: element.resourceType,
      isGroupConnected: element.isGroupConnected,
      connectedGroupId: element.groupId,
    };
    this.sharedService.toggleLifecycleModal('Lifecycle', formData);
  }

  openDeleteResourceForm(element: ResourceDto) {
    const formData = {
      formRef: DeleteResourceFormComponent,
      displayName: element.displayName,
      id: element.id,
      resourceType: element.resourceType,
      isGroupConnected: element.isGroupConnected,
      connectedGroupId: element.groupId,
    };
    this.sharedService.toggleDeleteResourceModal('Delete Resource', formData);
  }

  openInNewTab(url: string) {
    window.open(url, '_blank');
  }

  protected readonly ResourceTypes = ResourceTypes;
  protected readonly ResourceStatus = ResourceStatus;
}

export function CustomPaginator(translate: TranslateService): MatPaginatorIntl {
  const paginatorIntl = new MatPaginatorIntl();

  const translateLabels = () => {
    paginatorIntl.itemsPerPageLabel = translate.instant(
      'PAGINATOR.ITEMS_PER_PAGE',
    );
    paginatorIntl.nextPageLabel = translate.instant('PAGINATOR.NEXT_PAGE');
    paginatorIntl.previousPageLabel = translate.instant(
      'PAGINATOR.PREVIOUS_PAGE',
    );
    paginatorIntl.firstPageLabel = translate.instant('PAGINATOR.FIRST_PAGE');
    paginatorIntl.lastPageLabel = translate.instant('PAGINATOR.LAST_PAGE');
    paginatorIntl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ): string => {
      if (length === 0 || pageSize === 0) {
        return `0 ${translate.instant('PAGINATOR.OF')} ${length}`;
      }

      const startIndex = page * pageSize;
      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;

      return `${startIndex + 1} – ${endIndex} ${translate.instant('PAGINATOR.OF')} ${length}`;
    };
  };

  translate.onLangChange.subscribe(() => {
    translateLabels();
    paginatorIntl.changes.next();
  });

  translateLabels();

  return paginatorIntl;
}
